import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@card-software/nexus-utilities';
import { Translation } from '../../../models/translation';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { BatchProxyService } from '../../../proxies/production/batch.proxy.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { AdditionEntry, AdditionEntryType, LotItem, MergeLotItemEntry } from '../../../models/inventory/lot-item';
import { LotItemSearchCommand, LotItemSearchResultItem } from '../../../models/inventory/lot-item-search';
import { Batch, BatchStatus } from '../../../models/production/production-work-order';
import { ImportantLocations, LocationNode } from '../../../models/location';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

const SEARCH_PAGE_SIZE = 20;
const SEARCH_MIN_LENGTH = 2;

interface SourceRow {
  form: FormGroup;
  lotItem: LotItem | null;
}

interface MergeHistoryRow {
  entry: AdditionEntry;
  sourceNumber: string;
}

@Component({
  selector: 'app-merge-lot-items-detail',
  templateUrl: './merge-lot-items-detail.component.html',
  styleUrl: './merge-lot-items-detail.component.scss',
})
export class MergeLotItemsDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly batchProxyService = inject(BatchProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private subscriptions: Subscription[] = [];
  private readonly receiverQuery$ = new Subject<string>();
  private readonly sourceQuery$ = new Subject<string>();

  readonly BatchStatus = BatchStatus;
  readonly searchMinLength = SEARCH_MIN_LENGTH;

  isLoading = signal(0);
  locations: LocationNode[] = [];

  receiverSearch = new FormControl<LotItemSearchResultItem | null>(null);
  receiverOptions = signal<LotItemSearchResultItem[]>([]);
  receiver = signal<LotItem | null>(null);

  sourceOptions = signal<LotItemSearchResultItem[]>([]);
  sourceRows = signal<SourceRow[]>([]);
  batchesById = signal<Record<string, Batch>>({});
  mergeHistory = signal<MergeHistoryRow[]>([]);
  private formState = signal(0);

  private readonly translations = signal({
    Merged: { es: 'Los lotes se unieron correctamente', en: 'Lot items merged successfully' },
    Success: { es: 'Éxito', en: 'Success' },
  });

  receiverBatch = computed(() => this.batchFor(this.receiver()));

  isReceiverBatchValid = computed(() => this.receiverBatch()?.status === BatchStatus.Completed);

  totalToMerge = computed(() => {
    this.formState();
    return this.sourceRows().reduce((sum, row) => sum + (Number(row.form.value.quantity) || 0), 0);
  });

  canSave = computed(() => {
    this.formState();
    const rows = this.sourceRows();
    if (!this.receiver() || !this.isReceiverBatchValid() || rows.length === 0) return false;

    const sourceIds = rows.map((row) => row.lotItem?.id);
    const hasDuplicates = new Set(sourceIds).size !== sourceIds.length;
    return !hasDuplicates && rows.every((row) => row.form.valid && row.lotItem && this.isSourceBatchValid(row.lotItem));
  });

  ngOnInit(): void {
    // switchMap drops the response of a search the user already typed past, so older results never overwrite newer ones.
    this.subscriptions.push(
      this.receiverQuery$.pipe(switchMap((query) => this.searchLotItems(query))).subscribe((items) => this.receiverOptions.set(items)),
      this.sourceQuery$
        .pipe(switchMap((query) => this.searchLotItems(query, this.receiver()?.itemCode)))
        .subscribe((items) => this.sourceOptions.set(items.filter((item) => item.number !== this.receiver()?.number))),
    );

    this.startLoading();
    this.locationProxyService
      .batchGetByCodes([ImportantLocations.PC_PRODUCTION, ImportantLocations.PC_FINISHED_GOODS])
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.locations = response.data ?? [];
        },
      });

    const receiverId = this.route.snapshot.queryParamMap.get('id');
    if (receiverId) {
      this.loadReceiver(receiverId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  canEdit(): boolean {
    return AuthService.hasRole(UserGroup.MergeLotItemAdmin);
  }

  cancel(): void {
    this.router.navigate(['/merge-lot-items']);
  }

  startLoading(): void {
    this.isLoading.update((value) => value + 1);
  }

  stopLoading(): void {
    this.isLoading.update((value) => Math.max(0, value - 1));
  }

  searchReceiver(event: AutoCompleteCompleteEvent): void {
    this.receiverQuery$.next(event.query.trim());
  }

  searchSource(event: AutoCompleteCompleteEvent): void {
    if (!this.receiver()) return;
    this.sourceQuery$.next(event.query.trim());
  }

  // Below minLength PrimeNG stops searching, so a request still in flight would land stale results: cancel it.
  cancelShortSearch(event: KeyboardEvent, target: 'receiver' | 'source'): void {
    if ((event.target as HTMLInputElement).value.trim().length >= SEARCH_MIN_LENGTH) return;
    (target === 'receiver' ? this.receiverQuery$ : this.sourceQuery$).next('');
  }

  onReceiverSelected(event?: AutoCompleteSelectEvent): void {
    this.sourceRows.set([]);
    if (!event?.value?.number) {
      this.receiverQuery$.next('');
      this.receiver.set(null);
      this.mergeHistory.set([]);
      return;
    }

    this.startLoading();
    this.lotItemProxyService
      .getByLotItemNumber(event.value.number)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data?.id) {
            this.loadReceiver(response.data.id);
          }
        },
      });
  }

  addSourceRow(): void {
    const form = new FormGroup({
      source: new FormControl<LotItemSearchResultItem | null>(null, Validators.required),
      quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    });
    const row: SourceRow = { form, lotItem: null };
    this.subscriptions.push(form.valueChanges.subscribe(() => this.formState.update((value) => value + 1)));
    this.sourceRows.update((rows) => [...rows, row]);
  }

  removeSourceRow(index: number): void {
    this.sourceRows.update((rows) => rows.filter((_, i) => i !== index));
    this.formState.update((value) => value + 1);
  }

  onSourceSelected(row: SourceRow, event?: AutoCompleteSelectEvent): void {
    row.lotItem = null;
    this.formState.update((value) => value + 1);
    if (!event?.value?.number) {
      this.sourceQuery$.next('');
      return;
    }

    this.startLoading();
    this.lotItemProxyService
      .getByLotItemNumber(event.value.number)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const lotItem = response.data;
          if (!lotItem) return;
          row.lotItem = lotItem;
          const quantityControl = row.form.get('quantity')!;
          quantityControl.setValidators([Validators.required, Validators.min(0.01), this.maxQuantityValidator(lotItem.netQuantity)]);
          quantityControl.setValue(lotItem.netQuantity);
          this.loadBatches([lotItem.batchReference]);
        },
      });
  }

  batchFor(lotItem: LotItem | null): Batch | null {
    return lotItem?.batchReference ? (this.batchesById()[lotItem.batchReference] ?? null) : null;
  }

  isSourceBatchValid(lotItem: LotItem): boolean {
    const batch = this.batchFor(lotItem);
    return batch?.status === BatchStatus.Closed && lotItem.batchReference !== this.receiver()?.batchReference;
  }

  batchStatusLabel(batch: Batch | null): string {
    return batch ? (BatchStatus[batch.status] ?? String(batch.status)) : '—';
  }

  save(): void {
    const receiver = this.receiver();
    if (!this.canSave() || !receiver?.id) return;

    const request: MergeLotItemEntry[] = this.sourceRows().map((row) => ({
      sourceLotItemId: row.lotItem!.id!,
      quantity: Number(row.form.value.quantity),
    }));

    this.startLoading();
    this.lotItemProxyService
      .mergeLotItems(receiver.id, request)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: () => {
          const lang = (this.translateService.currentLang || 'en') as keyof Translation;
          GlobalMessageUtil.success(this.translations().Success[lang], this.translations().Merged[lang]);
          this.sourceRows.set([]);
          this.loadReceiver(receiver.id!);
        },
      });
  }

  private loadReceiver(id: string): void {
    this.startLoading();
    this.lotItemProxyService
      .getLotItemById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const lotItem = response.data;
          this.receiver.set(lotItem ?? null);
          if (!lotItem) return;
          this.receiverSearch.setValue({ number: lotItem.number } as LotItemSearchResultItem, { emitEvent: false });
          this.loadBatches([lotItem.batchReference]);
          this.loadMergeHistory(lotItem);
        },
      });
  }

  private loadMergeHistory(lotItem: LotItem): void {
    const merges = (lotItem.additionEntries ?? []).filter((entry) => entry.type === AdditionEntryType.Merge);
    if (merges.length === 0) {
      this.mergeHistory.set([]);
      return;
    }

    const sourceIds = [...new Set(merges.map((entry) => entry.initiator.reference))];
    this.startLoading();
    this.lotItemProxyService
      .batchLotItem(sourceIds)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const numberById = new Map((response.data ?? []).map((source) => [source.id, source.number ?? '']));
          this.mergeHistory.set(
            merges.map((entry) => ({ entry, sourceNumber: numberById.get(entry.initiator.reference) ?? entry.initiator.reference })),
          );
        },
      });
  }

  private loadBatches(batchReferences: (string | undefined)[]): void {
    const missing = batchReferences.filter((reference): reference is string => !!reference && !this.batchesById()[reference]);
    if (missing.length === 0) return;

    this.startLoading();
    this.batchProxyService
      .getByIds(missing)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const batches = response.data ?? [];
          this.batchesById.update((current) => ({ ...current, ...Object.fromEntries(batches.map((batch) => [batch.id, batch])) }));
          this.formState.update((value) => value + 1);
        },
      });
  }

  private searchLotItems(query: string, itemCode?: string): Observable<LotItemSearchResultItem[]> {
    if (!query) return of([]);

    const request: LotItemSearchCommand = {
      searchTerm: query,
      itemCodes: itemCode ? [itemCode] : undefined,
      locationPaths: this.locations.map((location) => location.path!),
      excludeZeroBalances: true,
    };

    return this.lotItemProxyService.search(request, 1, SEARCH_PAGE_SIZE).pipe(
      map((response) => response.data?.items ?? []),
      catchError(() => of([])),
    );
  }

  private maxQuantityValidator(max: number) {
    return (control: AbstractControl): ValidationErrors | null =>
      Number(control.value) > max + 0.001 ? { exceedsAvailable: { max } } : null;
  }
}
